//ES6 implementation of Jack Lawsons excellent Mediator.
//In my version Mediator is a static class. There can be only one.

const uuidv4 = () =>
    // @ts-ignore
    ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (
            c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
    )

class Subscriber {
    constructor(fn, options, context) {
        this.id = uuidv4()
        this.fn = fn
        this.options = options
        this.context = context
        this.channel = null
    }

    // Mediator.update on a subscriber instance can update its function,context,
    // or options object. It takes in an object and looks for fn, context, or
    // options keys.
    update(options) {
        if (options) {
            this.fn = options.fn || this.fn
            this.context = options.context || this.context
            this.options = options.options || this.options
            if (
                this.channel &&
                this.options &&
                this.options.priority !== undefined
            ) {
                this.channel.setPriority(this.id, this.options.priority)
            }
        }
    }
}

// A Mediator channel holds a list of sub-channels and subscribers to be fired
// when Mediator.publish is called on the Mediator instance. It also contains
// some methods to manipulate its lists of data; only setPriority and
// StopPropagation are meant to be used. The other methods should be accessed
// through the Mediator instance.

class Channel {
    subscribers
    channels
    parent

    constructor(namespace, parent) {
        this.namespace = namespace || ''
        this.subscribers = []
        this.channels = {}
        this.parent = parent
        this.stopped = false
    }

    // Sort function for sorting the channels based on their priority. The sort will
    // place the channel with the highest priority in the first place of the array,
    // all other channels will be added in descending order. i.g. a channel with a
    // priority of 9 will be placed before a channel with a priority of 3
    channelPrioritySort(a, b) {
        if (a.options.priority > b.options.priority) {
            return -1
        } else if (a.options.priority < b.options.priority) {
            return 1
        }
        return 0
    }

    addSubscriber(fn, options, context) {
        // Make sure there is always an options object
        if (!options) {
            options = { priority: 0 }
        }

        // Cheap hack to either parse as an int or turn it into 0. Runs faster
        // in many browsers than parseInt with the benefit that it won't
        // return a NaN.
        options.priority = options.priority | 0

        var subscriber = new Subscriber(fn, options, context)
        subscriber.channel = this

        this.subscribers.push(subscriber)
        this.subscribers.sort(this.channelPrioritySort)

        return subscriber
    }

    // The channel instance is passed as an argument to the mediator subscriber,
    // and further subscriber propagation can be called with
    // channel.StopPropagation().
    stopPropagation() {
        this.stopped = true
    }

    getSubscriber(identifier) {
        for (let subscriber of this.subscribers) {
            if (subscriber.id === identifier || subscriber.fn === identifier) {
                return subscriber
            }
        }
    }

    // Channel.setPriority is useful in updating the order in which Subscribers
    // are called, and takes an identifier (subscriber id or named function) and
    // an array index. It will not search recursively through subchannels.

    setPriority(identifier, priority) {
        for (let subscriber of this.subscribers) {
            if (subscriber.id === identifier || subscriber.fn === identifier) {
                subscriber.options.priority = priority
                break
            }
        }

        this.subscribers.sort(this.channelPrioritySort)
    }

    addChannel(channel) {
        this.channels[channel] = new Channel(
            (this.namespace ? this.namespace + ':' : '') + channel,
            this
        )
    }

    hasChannel(channel) {
        return this.channels.hasOwnProperty(channel)
    }

    returnChannel(channel) {
        return this.channels[channel]
    }

    removeSubscriber(identifier, autoCleanup) {
        let x = this.subscribers.length - 1

        autoCleanup = typeof autoCleanup === 'undefined' ? true : autoCleanup

        // If we don't pass in an id, we're clearing all
        if (!identifier) {
            this.subscribers = []
        } else {
            // Going backwards makes splicing a whole lot easier.
            for (x; x >= 0; x--) {
                if (
                    this.subscribers[x].fn === identifier ||
                    this.subscribers[x].id === identifier
                ) {
                    this.subscribers[x].channel = null
                    this.subscribers.splice(x, 1)
                }
            }
        }

        // Check if we should attempt to automatically clean up the channel
        if (autoCleanup) {
            this.autoCleanChannel()
        }

        x = this.subscribers.length - 1

        // If we don't pass in an id, we're clearing all
        if (!identifier) {
            this.subscribers = []
            return
        }

        // Going backwards makes splicing a whole lot easier.
        for (x; x >= 0; x--) {
            if (
                this.subscribers[x].fn === identifier ||
                this.subscribers[x].id === identifier
            ) {
                this.subscribers[x].channel = null
                this.subscribers.splice(x, 1)
            }
        }
    }

    autoCleanChannel() {
        // First make sure there are no more subscribers, if there are we
        // can stop processing the channel any further
        if (this.subscribers.length !== 0) {
            return
        }

        // Next we need to make sure there are no more sub-channels that
        // have this channel as its parent channel. We try to iterate over the
        // properties of _channels and as soon as we find a property we've
        // set we will stop processing the channel any further
        for (let key in this.channels) {
            if (this.channels.hasOwnProperty(key)) {
                return
            }
        }

        // The last check before we can remove the channel is to see if it
        // has a parent. If there is no parent we don't want to remove this
        // channel
        if (this.parent != null) {
            // We need the ID by which this channel is known to its parent, it should
            // be the last part of the namespace. We need split the namespace up and
            // take its last element to pass along to the removeChannel method
            let path = this.namespace.split(':'),
                channelName = path[path.length - 1]
            // The parent of the current channel to remove this channel
            this.parent.removeChannel(channelName)
        }
    }

    removeChannel(channelName) {
        if (this.hasChannel(channelName)) {
            this.channels[channelName] = null
            delete this.channels[channelName]
            // After removing a sub-channel we can check if that makes this channel
            // suitable for auto clean up
            this.autoCleanChannel()
        }
    }

    // This will publish arbitrary arguments to a subscriber and then to parent
    // channels.

    publish(data) {
        let x = 0
        let y = this.subscribers.length
        let shouldCall = false
        let subscriber
        let subsBefore
        let subsAfter

        // Priority is preserved in the _subscribers index.
        for (x, y; x < y; x++) {
            // By default set the flag to false
            shouldCall = false
            subscriber = this.subscribers[x]

            if (!this.stopped) {
                subsBefore = this.subscribers.length
                if (
                    subscriber.options !== undefined &&
                    typeof subscriber.options.predicate === 'function'
                ) {
                    if (
                        subscriber.options.predicate.apply(
                            subscriber.context,
                            data
                        )
                    ) {
                        // The predicate matches, the callback function should be called
                        shouldCall = true
                    }
                } else {
                    // There is no predicate to match, the callback should always be called
                    shouldCall = true
                }
            }

            // Check if the callback should be called
            if (shouldCall) {
                // Check if the subscriber has options and if this include the calls options
                if (
                    subscriber.options &&
                    subscriber.options.calls !== undefined
                ) {
                    // Decrease the number of calls left by one
                    subscriber.options.calls--
                    // Once the number of calls left reaches zero or less we need to remove the subscriber
                    if (subscriber.options.calls < 1) {
                        this.removeSubscriber(subscriber.id)
                    }
                }
                // Now we call the callback, if this in turns publishes to the same channel it will no longer
                // cause the callback to be called as we just removed it as a subscriber
                subscriber.fn.apply(subscriber.context, data)

                subsAfter = this.subscribers.length
                y = subsAfter
                if (subsAfter === subsBefore - 1) {
                    x--
                }
            }
        }

        if (this.parent) {
            this.parent.publish(data)
        }

        this.stopped = false
    }
}

export class Mediator {
    static channels = new Channel('')
    static on = this.subscribe
    static bind = this.subscribe
    static emit = this.publish
    static trigger = this.publish
    static off = this.remove
    static one = this.once

    // Finally, expose it all.
    static Channel = Channel
    static Subscriber = Subscriber
    static version = '0.10.1'

    // Mediator is the interface through which events are registered
    // and removed from publish channels.

    // Returns a channel instance based on namespace, for example
    // application:chat:message:received. If readOnly is true we
    // will refrain from creating non existing channels.

    static getChannel(namespace, readOnly) {
        const namespaceHierarchy = namespace.split(':')
        let channel = this.channels
        let x = 0
        let y = namespaceHierarchy.length

        if (namespace === '') {
            return channel
        }

        if (namespaceHierarchy.length > 0) {
            for (x, y; x < y; x++) {
                if (!channel.hasChannel(namespaceHierarchy[x])) {
                    if (readOnly) {
                        break
                    } else {
                        channel.addChannel(namespaceHierarchy[x])
                    }
                }

                channel = channel.returnChannel(namespaceHierarchy[x])
            }
        }

        return channel
    }

    // Pass in a channel namespace, function to be called, options, and context
    // to call the function in to Subscribe. It will create a channel if one
    // does not exist. Options can include a predicate to determine if it
    // should be called (based on the data published to it) and a priority
    // index.

    static subscribe(channelName, fn, options, context) {
        const channel = this.getChannel(channelName || '', false)

        options = options || {}
        context = context || {}

        return channel.addSubscriber(fn, options, context)
    }

    // Pass in a channel namespace, function to be called, options, and context
    // to call the function in to Subscribe. It will create a channel if one
    // does not exist. Options can include a predicate to determine if it
    // should be called (based on the data published to it) and a priority
    // index.

    static once(channelName, fn, options, context) {
        options = options || {}
        options.calls = 1

        return this.subscribe(channelName, fn, options, context)
    }

    // Returns a subscriber for a given subscriber id / named function and
    // channel namespace

    static getSubscriber(identifier, channelName) {
        const channel = this.getChannel(channelName || '', true)
        // We have to check if channel within the hierarchy exists and if it is
        // an exact match for the requested channel
        if (channel.namespace !== channelName) {
            return null
        }

        return channel.getSubscriber(identifier)
    }

    // Remove a subscriber from a given channel namespace recursively based on
    // a passed-in subscriber id or named function.

    static remove(channelName, identifier, autoClean) {
        const channel = this.getChannel(channelName || '', true)

        if (channel.namespace !== channelName) {
            return false
        }

        channel.removeSubscriber(identifier, autoClean)
    }

    // Publishes arbitrary data to a given channel namespace. Channels are
    // called recursively downwards; a post to application:chat will post to
    // application:chat:receive and application:chat:derp:test:beta:bananas.
    // Called using Mediator.publish("application:chat", [ args ]);

    static publish(channelName, ...args) {
        const channel = this.getChannel(channelName || '', false)
        if (channel.namespace !== channelName) {
            return null
        }

        let arg = Array.prototype.slice.call(args, 0)

        arg.push(channel)

        channel.publish(args)
    }
}
